import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import useSWR from "swr";

import { PortalNetworkError, signIn } from "../auth";
import { useSupportReplies } from "../hooks/useSupportReplies";
import { api, type Me } from "../services/api";

import { AnnouncementBanner } from "./AnnouncementBanner";
import { ErrorBoundary } from "./ErrorBoundary";
import { FeedbackModal } from "./FeedbackModal";
import { Icon } from "./Icon";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { NotificationBell } from "./NotificationBell";
import { Sidebar } from "./Sidebar";

const OFFLINE_NOTICE = "You're offline. Your data will load when you reconnect.";
const SIGN_IN_NOTICE =
  "We couldn't refresh your sign-in. Check your connection, then try again.";

function useBrowserOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const markOnline = () => setOnline(true);
    const markOffline = () => setOnline(false);
    window.addEventListener("online", markOnline);
    window.addEventListener("offline", markOffline);
    return () => {
      window.removeEventListener("online", markOnline);
      window.removeEventListener("offline", markOffline);
    };
  }, []);
  return online;
}

export function AppShell({ children }: { children: ReactNode }) {
  // Below the mobile breakpoint the sidebar collapses to a slide-in drawer
  // toggled by the hamburger; the overlay and any nav tap close it.
  const [navOpen, setNavOpen] = useState(false);
  // The top-right ⓘ button opens the Send-feedback form on any page (it
  // replaced the old "My Feedback" nav entry; the full ticket history lives in
  // Settings → Help & Support).
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const location = useLocation();
  // Same /api/me cache as the sidebar, so this does not add a request.
  // The bell shows for every signed-in user (#191), but not during View as,
  // which stays a read-only preview. isStaff only picks where a row's View
  // link goes.
  const { data: me, error: meError } = useSWR<Me>("/api/me", () => api.me());
  const online = useBrowserOnline();
  const signInRefreshFailed = meError instanceof PortalNetworkError;
  const isStaff = me?.role === "iquest_admin" || me?.role === "service_desk";
  const bellEnabled = !!me && !me.impersonating;
  // On phones the user pill (and its reply dot) sits inside the closed drawer,
  // so the menu button carries the dot until the drawer is opened.
  const { count: supportReplies, label: supportReplyLabel } = useSupportReplies(me);
  const navDot = supportReplies > 0 && !navOpen;

  return (
    <div className="app-shell">
      <button
        type="button"
        className="mobile-nav-toggle"
        aria-label={
          navOpen
            ? "Close navigation"
            : navDot
              ? `Open navigation, ${supportReplyLabel.toLowerCase()} from iQuest`
              : "Open navigation"
        }
        aria-expanded={navOpen}
        onClick={() => setNavOpen((o) => !o)}
      >
        {navOpen ? "×" : "☰"}
        {navDot && <span className="reply-dot" aria-hidden="true" />}
      </button>
      {navOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}
      <Sidebar isOpen={navOpen} onNavigate={() => setNavOpen(false)} />
      <div className="main-content">
        {/* Pinned at the very top of the content column so a staff "view as"
            session is unmissable above everything else, including the topbar. */}
        <ImpersonationBanner />
        {signInRefreshFailed && (
          <div className="info-banner warning auth-network-notice" role="status">
            <p>{online ? SIGN_IN_NOTICE : OFFLINE_NOTICE}</p>
            {online && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void signIn()}
              >
                Sign in
              </button>
            )}
          </div>
        )}
        {/* Slim sticky bar: the notifications bell, then the global
            help-and-feedback ⓘ at the top-right of every page. It sits in
            normal flow above the banner and content, so it never overlaps a
            page-header action or a banner's dismiss button (which a
            viewport-fixed button would). */}
        <header className="app-topbar">
          <NotificationBell enabled={bellEnabled} isStaff={isStaff} />
          <button
            type="button"
            className="topbar-help"
            aria-label="Help and feedback"
            title="Help and feedback"
            onClick={() => setFeedbackOpen(true)}
          >
            <Icon name="info" size={20} />
          </button>
        </header>
        <AnnouncementBanner />
        <main className="content">
          {/* A crash in a page (or a modal it renders) shows a recoverable
              fallback instead of blanking the whole app; the sidebar/nav
              survive. Keyed on the path so navigating away clears it. */}
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </main>
      </div>
      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
